import type { CertGenParams } from '@yandex-int/k-common';
import type { IntlShape } from 'react-intl';

import { formatCityDate } from '../../utils/date/format-city-date';

interface Props extends CertGenParams {
    slug: string;
    intl: IntlShape;
    issue_date?: string;
}

// eslint-disable-next-line complexity
export const TeacherReserveCandidate = ({
    school_name: schoolName,
    first_name: firstName,
    middle_name: middleName,
    last_name: lastName,
    slug,
    intl,
    issue_date: issueDate,
}: Props) => {
    const awardDescription = intl.formatMessage({ id: `teacherReserveCert.description.${slug}` });

    let image = 'https://e7n.s3.yandex.net/static/certificates/teacher-reserve/back_candidate.png';
    const city = 'Москва';
    let date = issueDate;
    let fallbackDate;
    let subtitle = 'подтверждает, что';
    let formattedDateOverride = '';
    let title = 'Сертификат';
    let titleAdditionalClass = '';
    let subtitleAdditionalClass = '';
    let personalInfoAdditionalClass = '';
    let sealAdditionalClass = '';
    let signer = 'Растворов Д. А.';
    let signerPosition = 'Директор по взаимодействию<br />с органами власти в сфере<br />образования ООО «Яндекс»';
    let sign = 'https://e7n.s3.yandex.net/static/certificates-images/sign_da.png';

    switch (slug) {
        case 'TRMENTOR':
            image = 'https://avatars.mds.yandex.net/get-schoolbook-images/5406199/WDW-l_iLUO9NAtGvI4OdL/orig';
            subtitle = 'получает';
            signer = 'Елена Краевская';
            signerPosition = 'Руководитель сервиса<br />Яндекс Учебник';
            sign = 'https://avatars.mds.yandex.net/get-schoolbook-images/6887102/-BaucEy53CUy-_gUtf7xC/orig';
            sealAdditionalClass = 'footer__seal2';
            break;
        case 'TeacherReserveProjectsParticipant':
            image = 'https://avatars.mds.yandex.net/get-schoolbook-images/6887102/7YSYw1Pa3qYcj2TUetTDG/orig';
            fallbackDate = '2024-01-30';
            subtitle = 'участника «Конкурса <br />образовательных проектов»';
            break;
        case 'TeacherReserveProjectsWinner':
            image = 'https://avatars.mds.yandex.net/get-schoolbook-images/6887102/u_9xgHrJ3tcuSaC7l1ptE/orig';
            fallbackDate = '2024-01-30';
            subtitle = 'победителя «Конкурса <br />образовательных проектов»';
            break;
        case 'TeacherReserveTeacherCompetitionParticipant':
            image = 'https://avatars.mds.yandex.net/get-schoolbook-images/5519467/6AiT_LYxB679nxtfC7x4D/orig';
            formattedDateOverride = 'Москва, апрель 2024';
            subtitle = 'участника «Педагогического конкурса<br />по созданию уроков по информатике»';
            break;
        case 'TeacherReserveTeacherCompetitionWinner':
            image = 'https://avatars.mds.yandex.net/get-schoolbook-images/6887102/qBv6Om1EUYV-gQMm-XmnO/orig';
            formattedDateOverride = 'Москва, апрель 2024';
            subtitle = 'победителя «Педагогического конкурса<br />по созданию уроков по информатике»';
            break;
        case 'TeacherReserveTeacherAwardParticipant':
            image = 'https://avatars.mds.yandex.net/get-schoolbook-images/5519467/J68cddev_ZIMrfFU1Uy8R/orig';
            formattedDateOverride = 'Москва, июнь 2024';
            subtitle = 'Этот сертификат получает';
            title = 'Сертификат<br />участника';
            titleAdditionalClass = 'content__title_lines-2';
            subtitleAdditionalClass = 'content__subtitle_title-multilined';
            personalInfoAdditionalClass = 'content__personal-info_title-multilined';
            break;
        case 'TeacherReserveTeacherAwardAwardee':
            image = 'https://avatars.mds.yandex.net/get-schoolbook-images/5519467/_cTWpzRdfhrWiSskxMJ55/orig';
            formattedDateOverride = 'Москва, июнь 2024';
            subtitle = 'Этот сертификат получает';
            title = 'Сертификат<br />призёра';
            titleAdditionalClass = 'content__title_lines-2';
            subtitleAdditionalClass = 'content__subtitle_title-multilined';
            personalInfoAdditionalClass = 'content__personal-info_title-multilined';
            break;
        case 'TeacherReserveTeacherAwardWinner':
            image = 'https://avatars.mds.yandex.net/get-schoolbook-images/5577852/hr1Bw0HqQ1P1hg_qAc6Fv/orig';
            formattedDateOverride = 'Москва, июнь 2024';
            subtitle = 'Этот сертификат получает';
            title = 'Сертификат<br />победителя';
            titleAdditionalClass = 'content__title_lines-2';
            subtitleAdditionalClass = 'content__subtitle_title-multilined';
            personalInfoAdditionalClass = 'content__personal-info_title-multilined';
            break;
        case 'TeacherReserveTeacherAwardAbsoluteWinner':
            image = 'https://avatars.mds.yandex.net/get-schoolbook-images/6887102/iyXtQbKOrtVWdWEShjAGU/orig';
            formattedDateOverride = 'Москва, июнь 2024';
            subtitle = 'Этот сертификат получает';
            title = 'Сертификат<br />абсолютного<br />победителя';
            titleAdditionalClass = 'content__title_lines-3';
            subtitleAdditionalClass = 'content__subtitle_title-multilined';
            personalInfoAdditionalClass = 'content__personal-info_title-multilined';
            sealAdditionalClass = 'footer__seal_right';
            break;
        default:
            break;
    }

    const formattedDate = formattedDateOverride || formatCityDate(city, date, fallbackDate);

    return `<!DOCTYPE html>
    <html lang="ru">
        <head>
            <title>Сертификат кандидата в кадровый резерв</title>
            <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            <style>
                @font-face {
                    font-family: 'YS Text';
                    src: url('https://yastatic.net/s3/home/fonts/ys/1/text-light.woff2') format('woff2'),
                        url('https://yastatic.net/s3/home/fonts/ys/1/text-light.woff') format('woff');
                    font-weight: 300;
                    font-style: normal;
                }

                @font-face {
                    font-family: 'YS Text';
                    src: url('https://yastatic.net/s3/home/fonts/ys/1/text-regular.woff2') format('woff2'),
                        url('https://yastatic.net/s3/home/fonts/ys/1/text-regular.woff') format('woff');
                    font-weight: 400;
                    font-style: normal;
                }

                @font-face {
                    font-family: 'YS Text';
                    src: url('https://yastatic.net/s3/home/fonts/ys/1/text-medium.woff2') format('woff2'),
                        url('https://yastatic.net/s3/home/fonts/ys/1/text-medium.woff') format('woff');
                    font-weight: 500;
                    font-style: normal;
                }

                @font-face {
                    font-family: 'YS Text';
                    src: url('https://yastatic.net/s3/home/fonts/ys/1/text-bold.woff2') format('woff2'),
                        url('https://yastatic.net/s3/home/fonts/ys/1/text-bold.woff') format('woff');
                    font-weight: bold;
                    font-style: normal;
                }

                @font-face {
                    font-family: 'YS Display';
                    font-weight: bold;
                    font-style: normal;
                    font-stretch: normal;

                    src: url('//yastatic.net/s3/home/fonts/ys/1/display-bold.woff2') format('woff2'),
                        url('//yastatic.net/s3/home/fonts/ys/1/display-bold.woff') format('woff');
                }

                @font-face {
                    font-family: 'YS Display';
                    font-weight: 800;
                    font-style: normal;
                    font-stretch: normal;

                    src: url('//yastatic.net/s3/home/fonts/ys/1/display-heavy.woff2') format('woff2'),
                        url('//yastatic.net/s3/home/fonts/ys/1/display-heavy.woff') format('woff');
                }

                @page {
                    size: 210mm 297mm;
                    margin: 0;
                    padding: 0;
                }

                @media print {
                    html,
                    body {
                        width: 210mm;
                        height: 297mm;

                        box-sizing: border-box;
                    }
                }

                html {
                    position: relative;

                    margin: 0;
                    padding: 0;

                    --background-color: #ffffff;
                    --text-color: #000000;
                    --second-text-color: #363e45;
                }

                body {
                    margin: 0;

                    color: var(--text-color);

                    font-family: 'YS Text', sans-serif;
                }

                .page {
                    position: relative;

                    overflow: hidden;

                    box-sizing: border-box;

                    width: 210mm;
                    height: 297mm;

                    background: var(--background-color);

                    page-break-after: always;
                }

                .page__background {
                    position: absolute;

                    width: 100%;
                    height: 100%;

                    overflow: hidden;

                    display: flex;

                    z-index: 1;
                }

                .page__background img {
                    display: block;
                }

                .page__content {
                    height: 100%;
                    width: 100%;
                }

                .content {
                    position: relative;
                    box-sizing: border-box;

                    display: flex;

                    z-index: 5;
                }

                .content {
                    display: flex;
                    flex-direction: column;

                    width: calc(210 / 595 * 304mm);

                    margin-left: calc(210 / 595 * 48mm);
                    margin-top: calc(297 / 842 * 42mm);

                    color: var(--text-color);
                }

                .content__logo {
                    width: calc(210 / 595 * 168mm);
                    height: calc(297 / 842 * 32mm);
                }

                .logo {
                    width: 100%;
                }

                .content__upper-title {
                    font-size: 16pt;
                    font-weight: 700;
                    line-height: 20.8pt;
                    color: var(--second-text-color);
                    text-transform: uppercase;
                    margin-top: calc(297 / 842 * 5mm);
                }

                .content__title {
                    height: calc(297 / 842 * 62mm);

                    margin-top: calc(297 / 842 * 30mm);

                    font-weight: 500;
                    font-family: 'YS Display', 'Arial', sans-serif;
                    letter-spacing: 1%;
                    font-size: 54pt;
                    color: var(--second-text-color);
                }

                .content__title_lines-2 {
                    font-size: 46pt;
                    line-height: 1.1;
                    height: calc(297 / 842 * 102mm);
                }

                .content__title_lines-3 {
                    font-size: 46pt;
                    line-height: 1.1;
                    height: calc(297 / 842 * 153mm);
                    margin-top: calc(297 / 842 * 24mm);
                }

                .content__subtitle {
                    font-family: 'YS Text', 'Arial', sans-serif;
                    font-style: normal;
                    font-weight: 400;
                    font-size: 14pt;
                    line-height: 14pt;
                    margin-top: calc(297 / 842 * 8mm);
                    color: var(--second-text-color);
                }

                .content__subtitle_title-multilined {
                    margin-top: calc(297 / 842 * 22mm);
                }

                .content__personal-info {
                    margin-top: calc(297 / 842 * 19mm);
                }

                .content__personal-info_title-multilined {
                    margin-top: calc(297 / 842 * 8mm);
                }

                .personal-info {
                    display: flex;
                    flex-direction: column;

                    height: calc(297 / 842 * 173mm);
                }

                .personal-info__name {
                    font-family: 'YS Text', 'Arial', sans-serif;
                    font-weight: 500;
                    font-size: 28pt;
                    line-height: 30.75pt;
                }

                .personal-info__school {
                    margin-top: calc(297 / 842 * 12mm);

                    font-family: 'YS Text', 'Arial', sans-serif;
                    font-style: normal;
                    font-weight: 400;
                    font-size: 14pt;
                    line-height: 16.8pt;
                }

                .content__description {
                    margin-top: calc(297 / 842 * 12mm);

                    font-family: 'YS Text', 'Arial', sans-serif;
                    font-weight: 500;
                    font-size: 14pt;

                    height: calc(297 / 842 * 68mm);
                }

                .footer {
                    position: absolute;

                    top: calc(297 / 842 * 538mm);
                    left: 0;
                }

                .footer__date {
                    margin-top: calc(297 / 842 * 38mm);

                    font-family: 'YS Text', 'Arial', sans-serif;
                    font-weight: 400;
                    font-size: 10pt;
                    line-height: 130%;
                }

                .footer__seal {
                    position: relative;
                    width: calc(210 / 595 * 107mm);
                    height: calc(297 / 842 * 107mm);
                }

                .footer__seal2 {
                    width: calc(210 / 595 * 137mm);
                    height: calc(297 / 842 * 108mm);
                }

                .footer__seal_right {
                    position: relative;
                    top: calc(210 / 595 * 97mm);
                    left: calc(297 / 842 * 177mm);
                }

                .seal {
                    height: 100%;
                    width: 100%;
                }

                .signature {
                    position: absolute;

                    height: calc(297 / 842 * 75mm);
                    width: calc(210 / 595 * 120mm);

                    top: calc(297 / 842 * 37mm);
                    left: calc(210 / 595 * 12mm);
                }

                .person {
                    display: flex;
                    flex-direction: column;

                    margin-top: calc(297 / 842 * 6mm);
                }

                .person__name {
                    font-family: 'YS Text', 'Arial', sans-serif;
                    font-weight: 700;
                    font-size: 12pt;
                    line-height: 15pt;
                }

                .person__position {
                    margin-top: calc(297 / 842 * 8mm);

                    font-family: 'YS Text', 'Arial', sans-serif;
                    font-weight: 400;
                    font-size: 12pt;
                    line-height: 14pt;
                }

                .footer__date {
                    font-size: 10pt;
                    font-weight: 400;
                    line-height: 13pt;

                    margin-top: calc(297 / 842 * 26mm);
                }
            </style>
        </head>
        <body>
            <div class="page">
                <div class="page__background">
                    <img src="${image}" />
                </div>
                <div class="page__content content content__main">
                    <div class="content__logo">
                        <img
                            class="logo"
                            src="https://e7n.s3.yandex.net/static/certificates/2022-autumn-diag-inf/logo.svg"
                            alt="Яндекс Учебник"
                        />
                    </div>
                    <div class="content__upper-title">Кадровый резерв<br />учителей информатики</div>
                    <div class="content__title ${titleAdditionalClass}">${title}</div>
                    <div class="content__subtitle ${subtitleAdditionalClass}">${subtitle}</div>
                    <div class="content__personal-info personal-info ${personalInfoAdditionalClass}">
                        <div class="personal-info__name name">
                            ${lastName ? `${lastName}` : ''}<br />
                            ${firstName ? `${firstName}` : ''}<br />
                            ${middleName ? `${middleName}` : ''}
                        </div>
                        <div class="personal-info__school school">${schoolName ? `${schoolName}` : ''}</div>
                    </div>
                    <div class="content__description">${awardDescription}</div>
                    <div class="content__footer footer">
                        <div class="footer__seal ${sealAdditionalClass}">
                            <img
                                class="seal"
                                src="${sign}"
                                alt="подпись"
                            />
                        </div>
                        <div class="footer__person person">
                            <div class="person__name">${signer}</div>
                            <div class="person__position">${signerPosition}</div>
                        </div>
                        <div class="footer__date">${formattedDate}</div>
                    </div>
                </div>
            </div>
        </body>
    </html>
    `;
};
